import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHead } from '@/admin-area/ui/PageHead';
import { PhonePrimary } from '@/admin-area/ui/PhonePrimary';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { deleteRoute, listRoutes, type SavedRoute } from './api';
import { formatKm } from './calc';
import { RouteForm } from './RouteForm';
import { useLoaded } from './use-loaded';

/** «Percorsi usuali»: the person's saved routes, one-way km (release 3): list, add, edit, delete. */
export function RoutesPage() {
  const { t } = useTranslation('expenses');
  const { state, reload } = useLoaded(listRoutes);
  const [editing, setEditing] = useState<SavedRoute | 'new' | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  const remove = async (route: SavedRoute): Promise<void> => {
    setFailed(false);
    const outcome = await deleteRoute(route.id);
    setConfirming(null);
    if (outcome.ok) reload();
    else setFailed(true);
  };
  const add = (
    <Button type="button" onClick={() => setEditing('new')}>
      <Plus />
      {t('mileage.routes.add')}
    </Button>
  );
  return (
    <section aria-label={t('mileage.routes.title')} className="flex flex-col gap-4">
      <PageHead
        title={t('mileage.routes.title')}
        summary={t('mileage.routes.summary')}
        actions={<span className="page-primary">{add}</span>}
      />
      {failed ? <p role="alert">{t('mileage.routes.deleteFailed')}</p> : null}
      {state.status === 'loading' ? <p role="status">{t('mileage.loading')}</p> : null}
      {state.status === 'failed' ? <p role="alert">{t('mileage.loadFailed')}</p> : null}
      {state.status === 'ready' && state.items.length === 0 ? (
        <p className="text-muted-foreground">{t('mileage.routes.empty')}</p>
      ) : null}
      {state.status === 'ready' ? (
        <RouteList
          routes={state.items}
          confirming={confirming}
          onEdit={setEditing}
          onAsk={setConfirming}
          onConfirm={(route) => void remove(route)}
        />
      ) : null}
      <PhonePrimary>{add}</PhonePrimary>
      <Sheet open={editing !== null} onOpenChange={(open) => (open ? undefined : setEditing(null))}>
        <SheetContent className="flex flex-col gap-4 p-4">
          <SheetTitle>
            {editing === 'new' ? t('mileage.routes.addTitle') : t('mileage.routes.editTitle')}
          </SheetTitle>
          <SheetDescription>{t('mileage.routes.formHelp')}</SheetDescription>
          {editing === null ? null : (
            <RouteForm
              {...(editing === 'new' ? {} : { route: editing })}
              onCancel={() => setEditing(null)}
              onSaved={() => {
                setEditing(null);
                reload();
              }}
            />
          )}
        </SheetContent>
      </Sheet>
    </section>
  );
}

function RouteActions({
  confirming,
  onEdit,
  onAsk,
  onCancel,
  onConfirm,
}: {
  readonly confirming: boolean;
  readonly onEdit: () => void;
  readonly onAsk: () => void;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}) {
  const { t } = useTranslation('expenses');
  if (confirming) {
    return (
      <span className="flex items-center gap-1">
        <span className="text-sm">{t('mileage.routes.deleteAsk')}</span>
        <Button
          type="button"
          variant="destructive"
          size="sm"
          className="max-lg:min-h-11"
          onClick={onConfirm}
        >
          {t('mileage.routes.delete')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="max-lg:min-h-11"
          onClick={onCancel}
        >
          {t('mileage.cancel')}
        </Button>
      </span>
    );
  }
  return (
    <span className="flex gap-1">
      <Button type="button" variant="ghost" size="sm" className="max-lg:min-h-11" onClick={onEdit}>
        {t('mileage.edit')}
      </Button>
      <Button type="button" variant="ghost" size="sm" className="max-lg:min-h-11" onClick={onAsk}>
        {t('mileage.routes.delete')}
      </Button>
    </span>
  );
}

function RouteList({
  routes,
  confirming,
  onEdit,
  onAsk,
  onConfirm,
}: {
  readonly routes: readonly SavedRoute[];
  readonly confirming: string | null;
  readonly onEdit: (route: SavedRoute) => void;
  readonly onAsk: (routeId: string | null) => void;
  readonly onConfirm: (route: SavedRoute) => void;
}) {
  const { t } = useTranslation('expenses');
  return (
    <ul aria-label={t('mileage.routes.title')} className="flex flex-col gap-2">
      {routes.map((route) => (
        <li
          key={route.id}
          className="border-border bg-card flex flex-wrap items-center justify-between gap-3 rounded-xl border p-3"
        >
          <span className="flex min-w-0 flex-col">
            <b className="truncate">
              {route.from} → {route.to}
            </b>
            <small className="text-muted-foreground tabular-nums">{formatKm(route.km)} km</small>
          </span>
          <RouteActions
            confirming={confirming === route.id}
            onEdit={() => onEdit(route)}
            onAsk={() => onAsk(route.id)}
            onCancel={() => onAsk(null)}
            onConfirm={() => onConfirm(route)}
          />
        </li>
      ))}
    </ul>
  );
}
